/**
 * ShopsMapPanel — the map column as an instrument, not a decorative tile.
 *
 * One framed panel with a ruled head that always says WHAT you are looking at
 * and WHAT IS SELECTED, and a body that is either the live 2GIS map or the
 * locator plan. Framing both states identically is what makes the map and the
 * ledger read as one instrument: the head names the seller the ledger has
 * open, and the plan pin / map camera follow the same selection.
 *
 * The fallback is treated as a first-class shipped state (it is what every
 * environment without a MapGL key actually shows, and what a failed SDK load
 * degrades to on a bazaar connection):
 *
 *   • no key   → says the live map is unavailable here, explains that the
 *                exact market and container are on every seller in the list,
 *                and offers the real recovery available without MapGL —
 *                opening the market on 2gis.kg.
 *   • failed   → says the map did not load and offers a retry that genuinely
 *                re-attempts the SDK (the parent bumps a remount key).
 *
 * Never a grey box with the word "unavailable" in it.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, RotateCw } from "lucide-react";

import { iconProps } from "@/lib/icons";
import type { SupportedLanguage } from "@/lib/i18n";
import type { SellerSummary } from "@/lib/garden-api";

import { ShopsMap } from "@/features/shops/ShopsMap";
import {
  SellerLocatorPlan,
  type LocatorLayout,
} from "@/features/shops/SellerLocatorPlan";
import { sellerStallCode } from "@/features/shops/SellerRow";

export interface ShopsMapPanelProps {
  sellers: SellerSummary[];
  layout: LocatorLayout;
  /** Render the real 2GIS map (a key is configured and it has not failed). */
  live: boolean;
  /** Remount key for the live map — bumped by a retry. */
  attempt: number;
  /** The SDK failed to load (as opposed to never having been configured). */
  failed: boolean;
  /** A MapGL key is baked in, so a retry can actually accomplish something. */
  hasKey: boolean;
  height: number | string;
  selectedSeller: SellerSummary | null;
  hoveredSellerId: string | null;
  onSelect: (seller: SellerSummary) => void;
  onHover: (sellerId: string | null) => void;
  onMapError: () => void;
  onRetry: () => void;
  onClearSelection: () => void;
  lang: SupportedLanguage;
}

export function ShopsMapPanel({
  sellers,
  layout,
  live,
  attempt,
  failed,
  hasKey,
  height,
  selectedSeller,
  hoveredSellerId,
  onSelect,
  onHover,
  onMapError,
  onRetry,
  onClearSelection,
  lang,
}: ShopsMapPanelProps): ReactElement {
  const { t } = useTranslation("shops");

  const placedCount = layout.points.length;
  // A 2GIS deep link to a real coordinate we hold — the only navigation this
  // product can honestly offer while MapGL is not available.
  const anchor = layout.points[0]?.seller;
  const geoUrl =
    anchor && anchor.lat !== null && anchor.lng !== null
      ? `https://2gis.kg/geo/${anchor.lng},${anchor.lat}`
      : undefined;

  return (
    <section
      data-testid="shops-map-panel"
      aria-label={live ? t("map.title") : t("plan.title")}
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-surface)]"
    >
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-[var(--g-rule)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase leading-none tracking-[0.09em] text-[var(--g-ink-2)]">
            {live ? t("map.title") : t("plan.title")}
          </p>

          {live ? (
            selectedSeller !== null ? (
              <p className="mt-1.5 min-w-0 text-[13px] leading-snug text-foreground">
                <span className="font-bold">
                  {selectedSeller.business_name}
                </span>
                <span className="g-data ml-2 text-[12px] text-[var(--g-ink-2)]">
                  {sellerStallCode(selectedSeller, lang)}
                </span>
              </p>
            ) : (
              <p className="g-data mt-1.5 text-[12.5px] leading-snug text-[var(--g-ink-2)]">
                {t("map.live", { count: placedCount })}
              </p>
            )
          ) : (
            <>
              <p className="mt-1.5 text-[14px] font-bold leading-snug text-foreground">
                {failed ? t("map.failedTitle") : t("map.unavailableTitle")}
              </p>
              <p className="mt-1 max-w-[52ch] text-[12.5px] leading-snug text-[var(--g-ink-2)]">
                {failed ? t("map.failedBody") : t("map.unavailableBody")}
              </p>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {live && selectedSeller !== null ? (
            <button
              type="button"
              onClick={onClearSelection}
              data-testid="shops-map-show-all"
              className="inline-flex h-9 items-center rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] px-3 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              {t("map.showAll")}
            </button>
          ) : null}

          {!live && hasKey ? (
            <button
              type="button"
              onClick={onRetry}
              data-testid="shops-map-retry"
              className="inline-flex h-9 items-center gap-1.5 rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] px-3 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              <RotateCw {...iconProps("sm")} />
              {t("retry")}
            </button>
          ) : null}

          {!live && !hasKey && geoUrl ? (
            <a
              href={geoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shops-map-open-2gis"
              className="inline-flex h-9 items-center gap-1.5 rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] px-3 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-[var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              <ExternalLink {...iconProps("sm")} />
              {t("map.openIn2gis")}
            </a>
          ) : null}
        </div>
      </header>

      <div className="min-h-0 flex-1">
        {live ? (
          <ShopsMap
            key={attempt}
            sellers={sellers}
            onSelect={onSelect}
            onError={onMapError}
            height={height}
            selectedSellerId={selectedSeller?.id ?? null}
          />
        ) : (
          <SellerLocatorPlan
            points={layout.points}
            unplacedCount={layout.unplacedCount}
            height={height}
            selectedSellerId={selectedSeller?.id ?? null}
            hoveredSellerId={hoveredSellerId}
            onSelect={onSelect}
            onHover={onHover}
            lang={lang}
          />
        )}
      </div>
    </section>
  );
}
